import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { findTarget } from "../dom";
import { useActiveTour } from "../hooks";
import { CHECKLIST, getTopic } from "../registry";
import { useHelpStore } from "../store";

import { HelpCard } from "./HelpBubble";

/** Marge du projecteur autour de la cible, en pixels. */
const PAD = 6;
/** Temps d'attente d'une cible après navigation avant de sauter l'étape. */
const TARGET_TIMEOUT_MS = 2500;
const POLL_MS = 120;
/** Sous cette largeur, la carte se fixe en bas de l'écran. */
const DOCK_BELOW_PX = 640;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function sameRect(a: Rect | null, b: Rect | null): boolean {
  if (!a || !b) return a === b;
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

/**
 * Moteur de visite guidée. Monté une fois dans le layout protégé ; inerte
 * tant qu'aucune visite n'est active.
 *
 *  - projecteur : voile sombre percé autour de la cible (ombre portée
 *    géante, aucun calcul de masque) ;
 *  - carte : Popover Radix ancré sur un rectangle virtuel qui suit la cible
 *    (défilement, redimensionnement) ; fixée en bas sous 640 px ;
 *  - navigation : la visite change de page toute seule, attend la cible, et
 *    saute une étape dont la cible n'existe pas (module masqué, INV-6) ;
 *  - clavier : → / Entrée suivant, ← précédent, Échap quitter ;
 *  - reprise : l'étape courante est persistée (rechargement, autre onglet).
 */
export function TourOverlay() {
  const active = useActiveTour();
  const setTourStep = useHelpStore((s) => s.setTourStep);
  const endTour = useHelpStore((s) => s.endTour);
  const tickChecklist = useHelpStore((s) => s.tickChecklist);
  const navigate = useNavigate();
  const location = useLocation();

  const [target, setTarget] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [docked, setDocked] = useState(() => typeof window !== "undefined" && window.innerWidth < DOCK_BELOW_PX);
  const navigatedFor = useRef<string | null>(null);

  const stepDef = active ? active.steps[active.step] : undefined;
  const total = active?.steps.length ?? 0;
  const stepKey = active ? `${active.tour.id}:${active.step}` : null;

  const finish = useCallback(
    (completed: boolean) => {
      if (!active) return;
      if (completed) {
        const item = CHECKLIST.find((c) => c.tour === active.tour.id);
        // Une étape à condition métier (ex. coordonnées de la clinique) ne
        // s'acquiert pas en terminant la visite.
        if (item && !item.doneWhen) tickChecklist(item.id, true);
        toast.success(`Visite « ${active.tour.title} » terminée.`);
      }
      endTour(completed);
    },
    [active, endTour, tickChecklist],
  );

  const goTo = useCallback(
    (index: number) => {
      if (!active) return;
      if (index >= total) finish(true);
      else if (index < 0) setTourStep(0);
      else setTourStep(index);
    },
    [active, total, finish, setTourStep],
  );

  // Visite active sans aucune étape visible : rien à montrer, on clôt.
  useEffect(() => {
    if (active && total === 0) endTour(false);
  }, [active, total, endTour]);

  // Page de l'étape : on y navigue une seule fois par étape.
  const wantedRoute = stepDef?.route ?? active?.tour.route ?? null;
  useEffect(() => {
    if (!stepKey || !wantedRoute) return;
    if (location.pathname === wantedRoute) return;
    if (navigatedFor.current === stepKey) return;
    navigatedFor.current = stepKey;
    navigate(wantedRoute);
  }, [stepKey, wantedRoute, location.pathname, navigate]);

  // Recherche de la cible (sondage court) ; absente → étape suivante.
  useEffect(() => {
    setTarget(null);
    setRect(null);
    if (!stepDef || !wantedRoute) return;
    if (location.pathname !== wantedRoute) return;
    let cancelled = false;
    const started = Date.now();
    const tick = () => {
      if (cancelled) return;
      const el = findTarget(stepDef.target);
      if (el) {
        setTarget(el);
        el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
        return;
      }
      if (Date.now() - started > TARGET_TIMEOUT_MS) {
        goTo((active?.step ?? 0) + 1);
        return;
      }
      window.setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
    };
    // `goTo`/`active` volontairement hors deps : une étape = une recherche.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, stepDef?.target, wantedRoute, location.pathname]);

  // Suivi du rectangle de la cible : défilement, redimensionnement, mutation.
  useLayoutEffect(() => {
    if (!target) return;
    const measure = () => {
      const r = target.getBoundingClientRect();
      const next: Rect = { top: r.top, left: r.left, width: r.width, height: r.height };
      setRect((prev) => (sameRect(prev, next) ? prev : next));
      setDocked(window.innerWidth < DOCK_BELOW_PX);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(target);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    const iv = window.setInterval(measure, 400);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      window.clearInterval(iv);
    };
  }, [target]);

  // Clavier.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Escape") {
        e.preventDefault();
        finish(false);
      } else if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        goTo(active.step + 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goTo(active.step - 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, finish, goTo]);

  // Ancre virtuelle : Radix relit le rectangle à chaque repositionnement.
  const virtualRef = useMemo(
    () => ({
      current: {
        getBoundingClientRect: () =>
          target?.getBoundingClientRect() ?? new DOMRect(window.innerWidth / 2, window.innerHeight / 2, 0, 0),
      },
    }),
    [target],
  );

  if (!active || !stepDef) return null;
  const topic = getTopic(stepDef.topic);
  if (!topic) return null;
  const isLast = active.step === total - 1;

  const footer = (
    <div className="flex items-center gap-2 border-t border-border px-3 py-2">
      <span className="text-xs tabular-nums text-muted" aria-live="polite">
        Étape {active.step + 1} sur {total}
      </span>
      <div className="mx-1 h-1 flex-1 bg-primary/10" aria-hidden>
        <div
          className="h-1 bg-primary transition-all duration-300"
          style={{ width: `${((active.step + 1) / total) * 100}%` }}
        />
      </div>
      {active.step > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => goTo(active.step - 1)}
          aria-label="Étape précédente"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        </Button>
      )}
      <Button size="sm" className="h-7 gap-1 px-2.5 text-xs" onClick={() => goTo(active.step + 1)} autoFocus>
        {isLast ? "Terminer" : "Suivant"}
        {!isLast && <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
      </Button>
    </div>
  );

  const card = (
    <div data-tour-card={active.tour.id} data-tour-step={active.step}>
      <HelpCard topic={topic} onClose={() => finish(false)} footer={footer} compact />
    </div>
  );

  return createPortal(
    <>
      {/* Projecteur : ombre géante autour de la cible = voile percé. */}
      {rect && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[60] rounded-sm ring-2 ring-primary-foreground/90 transition-all duration-200"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(12, 60, 80, 0.55)",
          }}
        />
      )}
      {!rect && <div aria-hidden className="pointer-events-none fixed inset-0 z-[60] bg-[rgba(12,60,80,0.35)]" />}

      {docked ? (
        <div
          role="dialog"
          aria-label={`Visite guidée : ${active.tour.title}`}
          className="fixed inset-x-2 bottom-12 z-[70] border border-border bg-surface text-foreground shadow-2xl"
        >
          {card}
        </div>
      ) : (
        <Popover open modal={false}>
          <PopoverAnchor virtualRef={virtualRef} />
          <PopoverContent
            side={stepDef.side ?? "bottom"}
            align="center"
            sideOffset={PAD + 10}
            collisionPadding={12}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
            className={cn("z-[70] w-[360px] p-0 text-foreground shadow-2xl")}
            role="dialog"
            aria-label={`Visite guidée : ${active.tour.title}`}
          >
            {card}
          </PopoverContent>
        </Popover>
      )}

      <button
        type="button"
        onClick={() => finish(false)}
        // Sous la navbar (h-16) : ne recouvre ni le menu utilisateur ni le
        // sélecteur de clinique, cibles de la visite « Découvrir MADA ».
        className="fixed right-3 top-[4.5rem] z-[70] inline-flex cursor-pointer items-center gap-1 border border-primary-foreground/30 bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground shadow hover:bg-primary/90"
        aria-label="Quitter la visite guidée"
      >
        <X className="h-3.5 w-3.5" aria-hidden /> Quitter la visite
      </button>
    </>,
    document.body,
  );
}
